<template>
  <div class="llm-chat-panel font-pretendard">
    <!-- header -->
    <div class="llm-chat-header d-flex align-center justify-space-between">
      <!-- 대화 목록 dropdown -->
      <v-menu
        v-model="conversationMenu"
        offset-y
        bottom
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on, value }">
          <div v-on="on"
            class="llm-chat-conversation-btn hover-bg-color d-flex align-center"
            style="--hover-bg-color: #F8F9FB"
          >
            <div class="llm-chat-conversation-title">
              {{ selectedConversationTitle }}
            </div>
            <v-icon :class="value ? 'up' : 'down'" style="font-size: 22px; color: #8A8DA8">
              mdi-chevron-down
            </v-icon>
          </div>
        </template>
        <v-card class="llm-chat-conversation-card">
          <div class="llm-chat-conversation-card-header d-flex align-center justify-space-between">
            <div>대화 목록</div>
            <div>{{ conversationList.length }}개</div>
          </div>
          <div class="llm-chat-conversation-list">
            <div
              v-for="conversation in conversationList"
              :key="conversation.id"
              class="llm-chat-conversation-item d-flex align-center"
              @click="selectConversation(conversation)"
            >
              <div class="llm-chat-conversation-item-text">
                <div class="llm-chat-conversation-item-title">
                  {{ conversation.title }}
                </div>
                <div class="llm-chat-conversation-item-date">
                  {{ conversation.date }}
                </div>
              </div>
              <button
                type="button"
                class="llm-chat-conversation-delete default-icon-btn align-center justify-center"
                @click.stop="deleteConversation(conversation)"
              >
                <v-icon style="font-size: 18px; color: #414458">mdi-trash-can-outline</v-icon>
              </button>
            </div>
          </div>
        </v-card>
      </v-menu>

      <div class="llm-chat-header-actions d-flex align-center">
        <!-- 새 대화 버튼 -->
        <div v-if="selectedConversation"
          @click="$emit('start-new-conversation')"
          class="llm-chat-header-icon-btn default-icon-btn d-flex align-center justify-center"
        >
          <v-icon style="font-size: 20px; color: #8A8DA8">
            mdi-plus
          </v-icon>
        </div>

        <!-- 취소 버튼 -->
        <div @click="$emit('close')"
          class="llm-chat-header-icon-btn default-icon-btn d-flex align-center justify-center"
        >
          <v-icon style="font-size: 20px; color: #8A8DA8">
            mdi-close
          </v-icon>
        </div>
      </div>
    </div>

    <!-- body -->
    <div ref="messageList" class="llm-chat-body">
      <!-- 무엇을 도와드릴까요? -->
      <div v-if="messages.length === 0" class="llm-chat-empty">
        <img src="/assets/llm_on.svg" class="llm-chat-empty-icon">
        <div class="llm-chat-empty-title">무엇을 도와드릴까요?</div>
        <div class="llm-chat-suggestion-list">
          <button
            v-for="suggestion in suggestionList"
            :key="suggestion"
            type="button"
            class="llm-chat-suggestion default-white-btn d-flex align-center"
            @click="clickSuggestion(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- 대화 내역 -->
      <div v-else class="llm-chat-message-list">
        <div
          v-for="(message, messageIdx) in messages"
          :key="message.id || messageIdx"
          class="llm-chat-message-row"
          :class="`is-${message.role}`"
        >
          <div v-if="message.timeText" class="llm-chat-message-time">
            {{ message.timeText }}
          </div>
          <div class="llm-chat-message" :class="`is-${message.role}`">
            <div v-if="message.role === 'error'" class="llm-chat-error-title">응답을 불러오지 못했습니다.</div>
            <div class="llm-chat-message-content">{{ message.content }}</div>
          </div>
        </div>
        <div v-if="loading" class="llm-chat-message-row is-assistant">
          <div class="llm-chat-message is-assistant">
            <div class="llm-chat-loading">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 입력창 -->
    <div class="llm-chat-input-wrap">
      <textarea
        ref="textarea"
        v-model="inputMessage"
        class="llm-chat-textarea"
        placeholder="메시지를 입력하세요."
        :disabled="loading"
        @keydown.enter="handleEnter"
      ></textarea>
      <div class="llm-chat-input-actions d-flex align-center justify-end">
        <button
          type="button"
          class="llm-chat-send-btn d-flex align-center justify-center"
          :class="canSend ? 'default-theme-btn is-active' : 'disabled-theme-btn is-disabled'"
          :disabled="!canSend"
          @click="sendMessage"
        >
          <v-icon style="font-size: 18px; color: #FFFFFF">mdi-send</v-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LlmChatFloatingPanel',

  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    conversationList: {
      type: Array,
      default: () => [],
    },
    selectedConversation: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      inputMessage: '',
      suggestionList: [
        '지금 보고 있는 데이터를 요약해줘',
        '이 데이터에서 주목할 부분을 알려줘',
        '리컬럼 사용 방법 알려줘',
      ],
      conversationMenu: false,
    }
  },

  computed: {
    canSend() {
      return this.inputMessage.trim().length > 0 && !this.loading
    },
    selectedConversationTitle() {
      return this.selectedConversation?.title || '새 대화'
    },
  },

  watch: {
    messages() {
      this.scrollToBottom()
    },
    loading() {
      this.scrollToBottom()
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.textarea) this.$refs.textarea.focus()
    })
  },

  methods: {
    handleEnter(event) {
      if (event.shiftKey) return
      event.preventDefault()
      this.sendMessage()
    },
    clickSuggestion(suggestion) {
      this.inputMessage = suggestion
      this.$nextTick(() => this.sendMessage())
    },
    selectConversation(conversation) {
      this.conversationMenu = false
      this.$emit('select-conversation', conversation)
    },
    deleteConversation(conversation) {
      this.$emit('delete-conversation', conversation)
    },
    sendMessage() {
      if (!this.canSend) return
      const message = this.inputMessage.trim()
      this.inputMessage = ''
      this.$emit('send', message)
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageList
        if (el) el.scrollTop = el.scrollHeight
      })
    },
  },
}
</script>

<style scoped>
.llm-chat-panel {
  position: fixed;
  right: 20px;
  bottom: 18px;
  z-index: 120;
  width: 366px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  height: 540px;
  display: flex;
  flex-direction: column;
  border: 1px solid #E8EAF2;
  border-radius: 16px;
  background-color: #FFFFFF;
  box-shadow: 4px 2px 12px 0px #0D0E1733;
  overflow: hidden;
  padding: 12px 14px;
}

.llm-chat-header {
  flex: 0 0 40px;
  min-height: 40px;
  gap: 12px;
}

.llm-chat-header-actions {
  flex: 0 0 auto;
  gap: 2px;
}

.llm-chat-header-icon-btn {
  width: 40px;
  height: 40px;
}

.llm-chat-conversation-btn {
  height: 30px;
  max-width: 220px;
  padding: 0px 6px 0px 10px;
  border-radius: 8px;
  gap: 10px;
  cursor: pointer;
  background-color: #FFFFFF;
}

.llm-chat-conversation-title {
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #414458;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.llm-chat-conversation-card {
  width: 260px;
  border-radius: 8px;
  background-color: #FFFFFF;
  box-shadow: 4px 2px 12px 0px #0D0E1733;
  overflow: hidden;
}

.llm-chat-conversation-card-header {
  height: 40px;
  padding: 0px 16px;
  border-bottom: 1px solid #EFF0F6;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #8A8DA8;
}

.llm-chat-conversation-list {
  max-height: 260px;
  overflow: hidden auto;
}

.llm-chat-conversation-item {
  min-height: 60px;
  padding: 10px 16px;
  border-bottom: 1px solid #EFF0F6;
  cursor: pointer;
  gap: 10px;
}

.llm-chat-conversation-item:last-child {
  border-bottom: 0;
}

.llm-chat-conversation-item:hover,
.llm-chat-conversation-item:focus-within {
  background-color: #F8F9FB;
}

.llm-chat-conversation-item-text {
  flex: 1;
  min-width: 0;
}

.llm-chat-conversation-item-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #414458;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.llm-chat-conversation-item-date {
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #ABAEC4;
  margin-top: 2px;
}

.llm-chat-conversation-delete {
  display: none;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
}

.llm-chat-conversation-item:hover .llm-chat-conversation-delete,
.llm-chat-conversation-item:focus-within .llm-chat-conversation-delete {
  display: flex;
}

.llm-chat-body {
  flex: 1;
  min-height: 0;
  overflow: hidden auto;
  padding: 12px 4px 20px;
}

.llm-chat-empty {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.llm-chat-empty-icon {
  width: 20px;
  height: 20px;
  margin-left: 12px;
  margin-bottom: 23px;
}

.llm-chat-empty-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 27px;
  color: #414458;
  margin-bottom: 22px;
}

.llm-chat-suggestion-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.llm-chat-suggestion {
  max-width: 100%;
  height: 29px;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #414458;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.llm-chat-message-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.llm-chat-message-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.llm-chat-message-row.is-user {
  align-items: flex-end;
}

.llm-chat-message-row.is-assistant,
.llm-chat-message-row.is-error {
  align-items: flex-start;
}

.llm-chat-message-time {
  width: 100%;
  text-align: center;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  color: #ABAEC4;
}

.llm-chat-message {
  max-width: 292px;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 400;
  line-height: 19px;
  color: #414458;
  white-space: pre-wrap;
  word-break: break-word;
}

.llm-chat-message.is-user {
  background-color: #F0F2FF;
}

.llm-chat-message.is-assistant {
  background-color: #F8F9FB;
}

.llm-chat-message.is-error {
  border: 1px solid #FFD5D2;
  background-color: #FFF6F5;
  color: #D92D20;
}

.llm-chat-error-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.llm-chat-loading {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 19px;
}

.llm-chat-loading span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: #ABAEC4;
  animation: llm-chat-loading 1.2s infinite ease-in-out;
}

.llm-chat-loading span:nth-child(2) {
  animation-delay: 0.16s;
}

.llm-chat-loading span:nth-child(3) {
  animation-delay: 0.32s;
}

.llm-chat-input-wrap {
  min-height: 110px;
  display: flex;
  flex-direction: column;
  border: 1px solid #E8EAF2;
  border-radius: 8px;
  background-color: #F8F9FB;
  padding: 14px 10px 8px;
}

.llm-chat-textarea {
  width: 100%;
  height: 46px;
  resize: none;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #414458;
  padding: 0px 8px;
  margin-bottom: 10px;
}

.llm-chat-textarea::placeholder {
  font-size: 14px;
  color: #ABAEC4;
}

.llm-chat-input-actions {
  height: 32px;
}

.llm-chat-send-btn {
  width: 36px;
  height: 32px;
  border: 0;
  border-radius: 8px;
}

.llm-chat-send-btn.is-active {
  background-color: #687EFF !important;
  cursor: pointer;
}

.llm-chat-send-btn.is-disabled {
  background-color: #DCE2FF;
  cursor: default;
}

@keyframes llm-chat-loading {
  0%, 80%, 100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 640px) {
  .llm-chat-panel {
    right: 16px;
    bottom: 16px;
    width: calc(100vw - 32px);
    max-height: calc(100vh - 80px);
  }
}
</style>
